import { FC, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PopupWithRequiredProps, popupFactory } from '@/factories'
import { PartialBy } from '@/models'

export const DETAILS_QUERY_PARAM = 'details'

// Note: keeps a details modal in the URL (`?details=<id>`) so it survives a refresh and can be
// shared. A query param (not a path segment) on purpose - PopupProvider closes every popup when the
// pathname changes.
export const useDetailsDeepLink = <P extends PopupWithRequiredProps>(
  modal: FC<P>,
  getModalProps: (id: number) => Omit<P, 'onClose'>,
) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const detailsId = Number(searchParams.get(DETAILS_QUERY_PARAM)) || null

  const openDetails = useCallback(
    (id: number) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(DETAILS_QUERY_PARAM, String(id))
          return next
        },
        { replace: true },
      ),
    [setSearchParams],
  )

  // Note: the popup provider calls onClose more than once (on close and after the animation), so
  // this has to stay idempotent.
  const clearDetails = useCallback(
    () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(DETAILS_QUERY_PARAM)
          return next
        },
        { replace: true },
      ),
    [setSearchParams],
  )

  useEffect(() => {
    const isOpen = !!popupFactory.checkIfOpen(modal)

    if (detailsId && !isOpen) {
      popupFactory.open(modal, {
        ...getModalProps(detailsId),
        onClose: clearDetails,
      } as PartialBy<P, 'onClose'>)
    } else if (!detailsId && isOpen) {
      popupFactory.closeIf(modal)
    }
  }, [detailsId, modal, getModalProps, clearDetails])

  return openDetails
}
